import { CompanyEnrichType } from "@/enum/company-enrich-type.enum";
import { EmploymentType, ScrapeLevel } from "@/enum/job.enum";
import { FundingMaType } from "@/enum/funding-ma.enum";
import { LiPostSort, TimeType } from "@/enum/li-post.enum";
import { LiPostType } from "@/enum/liprofile.enum";
import { ParallelAiType } from "@/enum/parallel-ai-type.enum";
import { CreateCompanyEnrich } from "@/pages/Private/pages/CompanyEnrich/schema/companyEnrich";
import { CreateFundingMa } from "@/pages/Private/pages/FundingMa/schema/funding-ma";
import { CreateJob } from "@/pages/Private/pages/Job/schema/job";
import { LiPost } from "@/pages/Private/pages/LiPost/schema/liPost";
import { CreateMap } from "@/pages/Private/pages/Map/schema/map";
import { CreateParallelAi } from "@/pages/Private/pages/ParallelAi/schema/parallelAi";

import { IcpPosition } from "@/pages/Private/pages/List/pages/ListEditDetails/enum/icp.enum";

import {
	AiCompanyConfig,
	AiNewsConfig,
	FundingConfig,
	GmapsConfig,
	HiringConfig,
	LeadDbConfig,
	LookalikeConfig,
	MnaConfig,
	NewInRoleConfig,
	PeopleAtCompanyConfig,
	TopicConfig,
} from "./onboardingListTypes";

import type { Icp } from "@/pages/Private/pages/List/pages/ListEditDetails/ListEditDetails";
import type { ApolloPeopleRequestParams } from "@/types/apollo";

/** Cap on leads pulled per matching LinkedIn post, as the in-app form defaults it. */
const TOPIC_MAX_PER_POST = 100;

/**
 * The semantic query is a plain-language description ranked against the company
 * embeddings; it is left out entirely when empty so it never widens a search.
 */
const semanticFilter = (semanticQuery: string): { semanticQuery?: string } =>
	semanticQuery.trim() ? { semanticQuery: semanticQuery.trim() } : {};

interface SourceContext {
	listId: number;
	listName: string;
}

/** JOBS — the in-app form always sends an employment type, so onboarding does too. */
export const buildJobPayload = (config: HiringConfig, { listId }: SourceContext): CreateJob => {
	if (!config.location) {
		throw new Error("hiring config requires a location");
	}

	return {
		name: config.query,
		query: config.query,
		employmentType: config.employmentType || EmploymentType.FULLTIME.toString(),
		jobScrapeType: ScrapeLevel.GET_ENRICHMENT,
		repeatDaily: false,
		inTitle: false,
		listId,
		location: { urn: config.location.id, title: config.location.name },
		positionTitles: config.positionTitles,
	};
};

/** COMPANY_ENRICH / LOOK_A_LIKE — seed domains plus the person filters. */
export const buildLookalikePayload = (
	config: LookalikeConfig,
	{ listId, listName }: SourceContext
): CreateCompanyEnrich => ({
	listId,
	name: listName,
	type: CompanyEnrichType.LOOK_A_LIKE,
	positionQuery: config.positionQuery,
	seniority: config.seniority,
	countries: config.countries,
	companyCountries: config.companyCountries,
	companyFilterSimilar: {
		domains: config.domains,
		employees: config.employees,
	},
	companyFilterSearch: semanticFilter(config.semanticQuery),
});

/** COMPANY_ENRICH / SEARCH — the compact version of the in-app lead-database filters. */
export const buildLeadDbPayload = (
	config: LeadDbConfig,
	{ listId, listName }: SourceContext
): CreateCompanyEnrich => ({
	listId,
	name: listName,
	type: CompanyEnrichType.SEARCH,
	positionQuery: config.positionQuery,
	seniority: config.seniority,
	department: config.department,
	countries: config.countries,
	companyCountries: config.companyCountries,
	companyFilterSearch: {
		...semanticFilter(config.semanticQuery),
		industries: config.industries,
		employees: config.employees,
		keywords: config.keywords,
	},
});

/** COMPANY_ENRICH / NEW_IN_ROLE — the window stays relative so the list re-resolves daily. */
export const buildNewInRolePayload = (
	config: NewInRoleConfig,
	{ listId, listName }: SourceContext
): CreateCompanyEnrich => ({
	listId,
	name: listName,
	type: CompanyEnrichType.NEW_IN_ROLE,
	newInRole: {
		changeTypes: config.changeTypes,
		window: { preset: config.windowPreset, fromDaysAgo: null, toDaysAgo: null },
	},
	seniority: config.seniority,
	department: config.department,
	countries: config.countries,
	companyFilterSearch: {
		industries: config.industries,
		employees: config.employees,
	},
});

/** LI_POST / BY_TOPIC — the capture toggles decide which engagements become leads. */
export const buildTopicPayload = (
	config: TopicConfig,
	{ listId }: SourceContext
): Partial<LiPost> => ({
	listId,
	name: config.query,
	query: config.query,
	type: LiPostType.BY_TOPIC,
	authorJobTitle: config.authorJobTitle || undefined,
	sortBy: LiPostSort.DATE_POSTED,
	timeType: TimeType.PERIOD,
	timeFrame: config.timeFrame,
	maxPerPost: TOPIC_MAX_PER_POST,
	repeatDaily: false,
	posts: config.capture.posts,
	likesToPost: config.capture.likes,
	commentsToPost: config.capture.comments,
	likesToComment: false,
	commentsToComment: false,
});

/** MAPS — country and city are the free-text names the Google Maps scraper takes. */
export const buildMapPayload = (
	config: GmapsConfig,
	{ listId, listName }: SourceContext
): CreateMap => ({
	listId,
	name: listName,
	query: config.query,
	country: config.country,
	city: config.city,
	mapScrapeType: ScrapeLevel.GET_ENRICHMENT,
	repeatDaily: false,
});

/** PARALLEL_AI / SEARCH — a one-off natural-language company search. */
export const buildAiCompanyPayload = (
	config: AiCompanyConfig,
	{ listId, listName }: SourceContext
): CreateParallelAi => ({
	listId,
	name: listName,
	query: config.query,
	type: ParallelAiType.SEARCH,
	frequency: null,
	processor: null,
	location: null,
});

/** PARALLEL_AI / MONITOR — the same search, re-run on the chosen cadence. */
export const buildAiNewsPayload = (
	config: AiNewsConfig,
	{ listId, listName }: SourceContext
): CreateParallelAi => ({
	listId,
	name: listName,
	query: config.query,
	type: ParallelAiType.MONITOR,
	frequency: config.frequency,
});

/** The one search profile of the onboarding form, in the shape `DefineIcp` saves. */
const icpOf = (people: PeopleAtCompanyConfig): Icp => {
	switch (people.position) {
		case IcpPosition.CUSTOM:
			return { position: people.position, title: people.title.trim() };
		case IcpPosition.DEPARTMENT_SENIORITY:
			return {
				position: people.position,
				seniority: people.seniority,
				department: people.department,
			};
		default:
			return { position: people.position };
	}
};

/**
 * The apollo config the details step writes: who to find at each qualifying
 * company, as the list's **first** search profile. Onboarding fills only that
 * one — the second and third stay open for the user to add in the app.
 */
export const buildPeopleAtCompanyConfig = (
	people: PeopleAtCompanyConfig
): ApolloPeopleRequestParams => ({
	enrichmentFilterConfig: { first: icpOf(people) },
	maxPerCompany: people.maxPerCompany,
});

/** FUNDING_ROUNDS — both modes share a shape; only FUNDING carries the rounds. */
export const buildFundingPayload = (
	config: FundingConfig | MnaConfig,
	{ listId, listName }: SourceContext
): CreateFundingMa => ({
	listId,
	name: listName,
	type: config.kind === "funding" ? FundingMaType.FUNDING : FundingMaType.MA,
	countries: config.countries,
	category: config.category,
	round: config.kind === "funding" ? config.round : [],
	lastXDays: config.lastXDays,
	dateFrom: null,
	dateTo: null,
});
